import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  Zap,
  Calendar,
  Pencil,
  Trash2,
  ArrowRightLeft,
} from "lucide-react";
import TeamLoadChart from "./TeamLoadChart";
import TeamHeadersChart from "./TeamHeadersChart";
import TeamStatCard from "./TeamStatCard";
import PeerSummaryCard from "./PeerSummaryCard";
import PlayerListPanel from "./PlayerListPanel";
import TeamFeedCard from "./TeamFeedCard";
import PaneFrame from "../common/PaneFrame";
import PeriodPicker from "components/common/PeriodPicker";
import { DEFAULT_HEADER_THRESHOLD_G } from "v2/utils/headers";
import { positionLabel } from "v2/constants/positions";
import { dateLocale } from "i18n";
import { MIN_PEER_SESSIONS } from "v2/utils/peerComparison";
import { parseDay } from "v2/utils/commandSearch";
import useWorkspaceRole from "v2/hooks/useWorkspaceRole";

// "2026-10-10" → "10 Oct" in the user's language; "" without a date.
const formatDay = (iso) =>
  parseDay(iso)?.toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "short",
  }) ?? "";

/**
 * Impact Load tab body: athlete rail on the left, overview pane on the right
 * (stat strip → charts → sessions/activity feed). Laid out by PaneFrame so
 * both sides end at the bottom of the window and only the panes scroll.
 */
const ImpactLoadTab = ({
  players,
  playersTotalCount,
  hasMorePlayers = false,
  playersLoadingMore = false,
  onLoadMorePlayers,
  selectedPlayerId,
  onPlayerSelect,
  onAddPlayer,
  onSortedPlayersChange,
  selectedPlayer,
  sessionType,
  setSessionType,
  period,
  setPeriod,
  teamLoadData,
  chartData,
  playerLoading = false,
  chartDataKey,
  loadStats,
  teamStats,
  team,
  peerStats,
  setShowEditPlayerModal,
  setShowSwitchTeamModal,
  setShowDeletePlayerModal,
  tracksHeaders = false,
  headerStats = { total: 0, perSession: [], loading: false },
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useWorkspaceRole();
  // Which chart is open when team headers are available: "load" | "headers".
  // Headers is team-wide only, so selecting an athlete snaps back to Load;
  // the tab bar itself stays in place (Headers disabled) so nothing shifts.
  const [chartTab, setChartTab] = useState("load");
  useEffect(() => {
    if (selectedPlayerId) setChartTab("load");
  }, [selectedPlayerId]);

  // The stat strip always has four cards, so nothing moves on selection.
  // Team view: Headers (heading sports) or Impacts, Impact load, Peak impact,
  // Sessions. Athlete view: Peer avg load, Impact load, Impacts, Sessions.
  const countLabel = tracksHeaders
    ? t("team.impactLoad.stats.headers")
    : t("team.impactLoad.stats.impacts");
  const peakImpact = teamStats?.peakImpact ?? null;

  return (
    <PaneFrame
      rail={
        <PlayerListPanel
          players={players}
          totalCount={playersTotalCount}
          hasMore={hasMorePlayers}
          loadingMore={playersLoadingMore}
          onLoadMore={onLoadMorePlayers}
          selectedPlayerId={selectedPlayerId}
          onPlayerSelect={onPlayerSelect}
          onAddPlayer={onAddPlayer}
          hideStats={teamLoadData.length === 0}
          onSortedPlayersChange={onSortedPlayersChange}
        />
      }
    >
      {/* Overview card: header, stat strip, charts */}
      <div className="rounded-xl border border-[#F3F4F6] bg-white">
        {/* Header - matches PlayerListPanel header */}
        <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Always two lines so the header height never changes */}
            <div className="min-h-[34px]">
              <h3 className="text-sm font-semibold text-[#111827]">
                {selectedPlayer
                  ? selectedPlayer.name
                  : t("team.impactLoad.title")}
              </h3>
              <p className="text-[11px] text-[#6B7280]">
                {selectedPlayer
                  ? positionLabel(selectedPlayer.position) ||
                    t("team.impactLoad.athlete")
                  : t("team.impactLoad.allAthletesOnRoster", {
                      value: team?.playerCount ?? 0,
                    })}
              </p>
            </div>
            {/* Edit/Switch/Delete buttons for selected player */}
            {selectedPlayer && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowEditPlayerModal(true)}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
                  title={t("team.impactLoad.editAthlete")}
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => setShowSwitchTeamModal(true)}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:border-[#C7D2FE] hover:bg-[#EEF2FF] hover:text-[#6366F1]"
                  title={t("team.impactLoad.switchTeam")}
                >
                  <ArrowRightLeft size={13} />
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setShowDeletePlayerModal(true)}
                    className="flex h-7 w-7 items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:border-[#FECACA] hover:bg-[#FEF2F2] hover:text-[#DC2626]"
                    title={t("team.impactLoad.deleteAthlete")}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Session type filter */}
            <div className="flex rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5">
              {[
                { value: "all", label: t("team.sessionTypes.all") },
                {
                  value: "training",
                  label: t("team.sessionTypes.training"),
                },
                { value: "match", label: t("team.sessionTypes.match") },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSessionType(opt.value)}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition duration-150 ${
                    sessionType === opt.value
                      ? "bg-white text-[#111827] shadow-sm"
                      : "text-[#6B7280] hover:text-[#374151]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <PeriodPicker value={period} onChange={setPeriod} />
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Stat Cards Row - show empty state when no data */}
          {teamLoadData.length === 0 ? (
            <div className="mb-4 grid grid-cols-4 gap-3">
              {[
                {
                  id: "count",
                  icon: Activity,
                  label: countLabel,
                  note: t("team.impactLoad.stats.noDataYet"),
                },
                {
                  id: "avgLoad",
                  icon: Activity,
                  label: t("team.impactLoad.stats.avgLoad"),
                  note: t("team.impactLoad.stats.noDataYet"),
                },
                {
                  id: "peak",
                  icon: Zap,
                  label: t("team.impactLoad.stats.peakImpact"),
                  note: t("team.impactLoad.stats.noDataYet"),
                },
                {
                  id: "sessions",
                  icon: Calendar,
                  label: t("team.impactLoad.stats.sessions"),
                  note: t("team.impactLoad.stats.noSessionsYet"),
                },
              ].map(({ id, icon: Icon, label, note }) => (
                <div
                  key={id}
                  className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] p-4"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F3F4F6]">
                      <Icon size={16} className="text-[#9CA3AF]" />
                    </div>
                    <span className="text-xs font-medium text-[#9CA3AF]">
                      {label}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#9CA3AF]">{note}</p>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-4 gap-3">
                {selectedPlayer ? (
                  <TeamStatCard
                    label={t("team.impactLoad.stats.peerAvgLoad")}
                    loading={playerLoading}
                    value={
                      peerStats?.loadDiff != null
                        ? Number(peerStats.peerAvgLoad).toLocaleString()
                        : "–"
                    }
                    unit={peerStats?.loadDiff != null ? "g" : undefined}
                    footerLeft={t("team.impactLoad.stats.sameSessions")}
                    footerRight={t("team.impactLoad.stats.sharedSessions", {
                      count: peerStats?.peerSessions ?? 0,
                    })}
                  />
                ) : tracksHeaders ? (
                  // Headers are impacts at or above the threshold: the wearable
                  // can't tell a header from other contact yet, so heading
                  // sports see this in place of a near-identical Impacts card.
                  <TeamStatCard
                    label={t("team.impactLoad.stats.headers")}
                    info={t("team.impactLoad.stats.headersInfo", {
                      value: DEFAULT_HEADER_THRESHOLD_G,
                    })}
                    loading={headerStats.loading}
                    value={Number(headerStats.total).toLocaleString()}
                    valueColor="#E07A3A"
                    footerLeft={t("team.impactLoad.stats.perAthletePerSession")}
                    footerRight={t("team.impactLoad.stats.avgValue", {
                      value: teamStats?.aboveThresholdAvg ?? 0,
                    })}
                  />
                ) : (
                  <TeamStatCard
                    label={t("team.impactLoad.stats.impacts")}
                    info={t("team.impactLoad.stats.impactsInfo")}
                    value={Number(teamStats?.totalEvents ?? 0).toLocaleString()}
                    footerLeft={t("team.impactLoad.stats.perAthletePerSession")}
                    footerRight={t("team.impactLoad.stats.avgValue", {
                      value: teamStats?.impactsAvg ?? 0,
                    })}
                  />
                )}
                <TeamStatCard
                  label={t("team.impactLoad.stats.avgLoad")}
                  info={t("team.impactLoad.stats.impactLoadInfo")}
                  loading={playerLoading}
                  value={
                    loadStats.avg == null
                      ? "–"
                      : Number(loadStats.avg).toLocaleString()
                  }
                  unit={loadStats.avg == null ? undefined : "g"}
                  subtitle={t("team.impactLoad.stats.total", {
                    value: `${Number(loadStats.total).toLocaleString()}g`,
                  })}
                  footerLeft={
                    selectedPlayer
                      ? t("team.impactLoad.stats.perSessionAvg")
                      : t("team.impactLoad.stats.perAthletePerSession")
                  }
                  footerRight={
                    selectedPlayer
                      ? peerStats?.loadDiff != null
                        ? t("team.impactLoad.stats.peerAvg", {
                            value: `${Number(
                              peerStats.peerAvgLoad
                            ).toLocaleString()}g`,
                          })
                        : t("team.impactLoad.stats.peerAvg", { value: "–" })
                      : t("team.impactLoad.stats.sessionsWithData", {
                          count: loadStats.sessions,
                        })
                  }
                  peerComparison={
                    selectedPlayer && peerStats?.loadDiff != null
                      ? {
                          value: peerStats.loadDiff,
                          peerValue: peerStats.peerAvgLoad,
                        }
                      : null
                  }
                />
                {selectedPlayer ? (
                  <TeamStatCard
                    label={t("team.impactLoad.stats.impacts")}
                    info={t("team.impactLoad.stats.impactsInfo")}
                    loading={playerLoading}
                    value={Number(selectedPlayer.events).toLocaleString()}
                    footerLeft={t("team.impactLoad.stats.inSelectedPeriod")}
                    footerRight={t("team.impactLoad.stats.peerAvg", {
                      value: selectedPlayer.peerAvgEvents,
                    })}
                    peerComparison={
                      // Same rule as the load comparison: no % from too
                      // few shared sessions.
                      peerStats?.loadDiff != null
                        ? {
                            value: peerStats.eventsDiff,
                            peerValue: selectedPlayer.peerAvgEvents,
                          }
                        : null
                    }
                  />
                ) : (
                  <TeamStatCard
                    label={t("team.impactLoad.stats.peakImpact")}
                    info={t("team.impactLoad.stats.peakImpactInfo")}
                    value={
                      peakImpact
                        ? Number(peakImpact.linear_g).toLocaleString(
                            undefined,
                            { maximumFractionDigits: 1 }
                          )
                        : "–"
                    }
                    unit={peakImpact ? "g" : undefined}
                    subtitle={
                      peakImpact?.player_name ??
                      t("team.impactLoad.stats.noImpactsYet")
                    }
                    footerLeft={peakImpact?.session_name ?? ""}
                    footerRight={formatDay(peakImpact?.session_date)}
                  />
                )}
                {selectedPlayer ? (
                  // The athlete's own count: sessions they were in, out of
                  // the team's sessions in the period (SPO-1537).
                  <TeamStatCard
                    label={t("team.impactLoad.stats.sessions")}
                    loading={playerLoading}
                    value={chartData.filter((d) => d.attended).length}
                    footerLeft={t("team.impactLoad.stats.attended")}
                    footerRight={t("team.impactLoad.stats.ofTeamSessions", {
                      count: chartData.length,
                    })}
                  />
                ) : (
                  <TeamStatCard
                    label={t("team.impactLoad.stats.sessions")}
                    loading={playerLoading}
                    value={chartData.length}
                    footerLeft={t("team.impactLoad.stats.inSelectedPeriod")}
                    footerRight={t("team.impactLoad.stats.athleteCount", {
                      value: team?.playerCount ?? 0,
                    })}
                  />
                )}
              </div>

              {/* Peer comparison slot — always the same height, whatever it
                  holds, so the chart below never moves: a skeleton while an
                  athlete loads, their comparison once it lands, a quiet note
                  if there is none, and a hint in the team view. */}
              <div className="mb-4 h-[106px]">
                {selectedPlayer ? (
                  playerLoading ? (
                    <div className="flex h-full animate-pulse items-center gap-4 rounded-xl border border-[#F3F4F6] bg-[#FAFAFA] p-4">
                      <div className="h-[72px] w-[72px] flex-shrink-0 rounded-xl bg-[#E5E7EB]" />
                      <div className="flex-1">
                        <div className="mb-2 h-3 w-28 rounded bg-[#E5E7EB]" />
                        <div className="mb-1.5 h-3 w-3/4 rounded bg-[#E5E7EB]" />
                        <div className="h-2.5 w-1/2 rounded bg-[#E5E7EB]" />
                      </div>
                    </div>
                  ) : peerStats?.loadDiff != null ? (
                    <PeerSummaryCard
                      playerName={selectedPlayer.name.split(" ")[0]}
                      percentageDiff={peerStats.loadDiff}
                      sessions={peerStats.peerSessions}
                    />
                  ) : (
                    <div className="flex h-full items-center gap-4 rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] p-4">
                      <div className="flex h-[72px] w-[72px] flex-shrink-0 items-center justify-center rounded-xl bg-[#F3F4F6]">
                        <Activity size={22} className="text-[#9CA3AF]" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#111827]">
                          {t("team.impactLoad.noPeerComparison.title")}
                        </p>
                        <p className="mt-1 text-[12px] leading-snug text-[#6B7280]">
                          {t("team.impactLoad.noPeerComparison.body", {
                            name: selectedPlayer.name.split(" ")[0],
                            min: MIN_PEER_SESSIONS,
                            value: peerStats?.peerSessions ?? 0,
                          })}
                        </p>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex h-full items-center gap-4 rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] p-4">
                    <div className="flex h-[72px] w-[72px] flex-shrink-0 items-center justify-center rounded-xl bg-[#F3F4F6]">
                      <Activity size={22} className="text-[#9CA3AF]" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#111827]">
                        {t("team.impactLoad.teamWideView.title")}
                      </p>
                      <p className="mt-1 text-[12px] leading-snug text-[#6B7280]">
                        {t("team.impactLoad.teamWideView.body")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Charts. With team headers on, Load and Headers are tabs in this
              card, one chart at a time, both kept mounted. */}
          {tracksHeaders && (
            <div className="mb-4 flex items-center gap-1 rounded-lg bg-[#F3F4F6] p-0.5">
              {[
                { key: "load", label: t("team.impactLoad.chartTabs.load") },
                {
                  key: "headers",
                  label: t("team.impactLoad.chartTabs.headers"),
                  teamOnly: true,
                },
              ].map((tab) => {
                const disabled = tab.teamOnly && !!selectedPlayerId;
                return (
                  <button
                    key={tab.key}
                    onClick={() => !disabled && setChartTab(tab.key)}
                    disabled={disabled}
                    title={
                      disabled
                        ? t("team.impactLoad.chartTabs.teamOnly")
                        : undefined
                    }
                    className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition duration-150 ${
                      chartTab === tab.key
                        ? "bg-white text-[#111827] shadow-sm"
                        : disabled
                        ? "cursor-not-allowed text-[#C4C8CE]"
                        : "text-[#6B7280] hover:text-[#374151]"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
          <div hidden={tracksHeaders && chartTab !== "load"}>
            <TeamLoadChart
              data={chartData}
              dataKey={chartDataKey}
              loading={playerLoading}
              comparisonMode={!!selectedPlayerId}
              playerName={selectedPlayer?.name?.split(" ")[0] || ""}
              sessionType={sessionType}
            />
          </div>
          {tracksHeaders && (
            <div hidden={chartTab !== "headers"}>
              <TeamHeadersChart
                data={headerStats.perSession}
                threshold={DEFAULT_HEADER_THRESHOLD_G}
                loading={headerStats.loading}
              />
            </div>
          )}
        </div>
      </div>

      {/* Sessions / Activity, one tabbed card */}
      <TeamFeedCard player={selectedPlayer} />
    </PaneFrame>
  );
};

// Memoised: the parent holds modal-open booleans and other state that don't
// affect this tab, so without this every modal toggle re-renders the tab and
// both (expensive) Recharts charts. All props passed in are referentially
// stable (state, memoised values, or useCallback handlers).
export default React.memo(ImpactLoadTab);
